import {
  useIonViewWillEnter,
  useIonViewWillLeave,
  IonPage,
  IonContent,
} from "@ionic/react";
import React, { useContext, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";

import { EOrderBy } from "../../../constants/boxDashboard";
import { RealizationStamp } from "../../../state";
import {
  Context,
  fetchGearboxRealizations,
  fetchAllFeedbacks,
  fetchBoxesDashboard,
  fetchGearboxStudents,
  createRealizationStampReaction,
  createFeedback,
  updateFeedback,
  updateStudentDetailModal,
  checkedTeacher,
  deleteFeedback,
  clearSuccessMessage,
  CREATE_FEEDBACK_SUCCESS_MESSAGE,
  UPDATE_FEEDBACK_SUCCESS_MESSAGE,
  DELETE_FEEDBACK_SUCCESS_MESSAGE,
  CREATE_REALIZATION_STAMP_REACTION_SUCCESS,
} from "../../../store/teacher";
import useWaitApiCall from "../../../hooks/useWaitApiCall";
import useIntersection from "../../../hooks/useIntersection";
import NavHeader from "../../../organisms/NavHeader";
import Realizations from "../../../templates/teacher/Realizations";
import styles from "../common.module.scss";

import Badge from "./Badge";

const RealizationsBoxPage = () => {
  const { contextState, dispatch } = useContext(Context);
  const [calling, peep] = useWaitApiCall(contextState);
  const { id } = useParams<{ id: string }>();
  const boxId = Number(id);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const isPageActiveRef = useRef(false);
  const isFirstLoadRef = useRef(true);

  const box = contextState.boxesDashboard.find(b => b.id === boxId) ?? null;

  const showStudentDetailFunc = useCallback(
    args => {
      dispatch(updateStudentDetailModal(args));
    },
    [dispatch],
  );

  const createFeedbackFunc = useCallback(
    (realization_id: number, content: string) => {
      dispatch(peep(createFeedback(realization_id, content)));
      const realization = contextState.realizations.find(
        r => r.id === realization_id,
      );
      if (realization && !realization.is_checked) {
        dispatch(peep(checkedTeacher(realization_id)));
      }
    },
    [dispatch, peep, contextState.realizations],
  );

  const createStampReactionFunc = useCallback(
    (realizationId: number, stamp: RealizationStamp) => {
      dispatch(
        peep(
          createRealizationStampReaction(realizationId, {
            type: "feedback",
            realization_stamp_id: stamp.id,
          }),
        ),
      );
      const realization = contextState.realizations.find(
        r => r.id === realizationId,
      );
      if (realization && !realization.is_checked) {
        dispatch(peep(checkedTeacher(realizationId)));
      }
    },
    [dispatch, peep, contextState.realizations],
  );

  const updateFeedbackFunc = useCallback(
    (feedbackId: number, content: string) => {
      dispatch(peep(updateFeedback(feedbackId, content)));
    },
    [dispatch, peep],
  );

  const deleteFeedbackFunc = useCallback(
    (feedbackId: number) => {
      dispatch(peep(deleteFeedback(feedbackId)));
    },
    [dispatch, peep],
  );

  useIonViewWillEnter(() => {
    isPageActiveRef.current = true;
    if (isFirstLoadRef.current) {
      dispatch(peep(fetchGearboxRealizations(boxId, 1)));
      isFirstLoadRef.current = false;
    }
    dispatch(fetchAllFeedbacks());
    dispatch(
      fetchBoxesDashboard({
        order_by: EOrderBy.LATEST_REALIZATION_CREATED_AT,
      }),
    );
    dispatch(peep(fetchGearboxStudents(boxId)));
  });

  useIonViewWillLeave(() => {
    isPageActiveRef.current = false;
  });

  useEffect(() => {
    // Ionicは前のページをアンマウントせずDOMに残すため、表示中のみ処理を実行する。
    if (!isPageActiveRef.current) return;
    if (
      contextState.success_message === CREATE_FEEDBACK_SUCCESS_MESSAGE ||
      contextState.success_message === UPDATE_FEEDBACK_SUCCESS_MESSAGE ||
      contextState.success_message === DELETE_FEEDBACK_SUCCESS_MESSAGE ||
      contextState.success_message === CREATE_REALIZATION_STAMP_REACTION_SUCCESS
    ) {
      dispatch(fetchAllFeedbacks());
      dispatch(clearSuccessMessage());
    }
  }, [dispatch, contextState.success_message]);

  useIntersection({
    target: loadMoreRef,
    enabled:
      !calling && contextState.pagy_info.page < contextState.pagy_info.pages,
    onIntersect: () => {
      dispatch(
        peep(fetchGearboxRealizations(boxId, contextState.pagy_info.page + 1)),
      );
    },
  });

  return (
    <IonPage>
      <NavHeader
        title={box?.title ? `${box.title}に提出されたストック` : ""}
        rightComponent={
          <Badge
            isArchived={box?.is_archived ?? false}
            nameStatus={box?.name_status ?? "publish"}
          />
        }
      />
      <IonContent className={`${styles.wrapper} ${styles.bgWhileBase}`}>
        <div className={styles.container}>
          <Realizations
            pageType="box"
            box={box}
            calling={calling}
            loadMoreRef={loadMoreRef}
            showStudentDetail={showStudentDetailFunc}
            createFeedback={createFeedbackFunc}
            createStampReaction={createStampReactionFunc}
            deleteFeedback={deleteFeedbackFunc}
            updateFeedback={updateFeedbackFunc}
          />
        </div>
      </IonContent>
    </IonPage>
  );
};

export default RealizationsBoxPage;
